/**
 * CODESMITH: detection of MCP servers used during a session.
 *
 * The transcript's tool parts carry MCP identity in `state.metadata`
 * (`mcpServerName`, `mcpProviderId`, `mcpServerUrl`; see mcpToolUtils.ts),
 * so the set of MCP integrations a conversation touched can be derived
 * entirely from already-fetched session data. The right sidebar's MCP
 * widget lists these servers with their current connection status.
 */
import type { AgentChatMessage } from "./types";
import { extractMcpToolMetadata } from "./mcpToolUtils";
import type { McpServer } from "@/hooks/useMcpServers";
import { normalizeMcpDisplayNameKey } from "@/components/settings/codesmith-mcp/formState";

export interface McpUsedServer {
  serverName: string;
  /** Provider slug for the brand icon; may be host-inferred (display-only). */
  providerId?: string;
  /**
   * Provider slug only when explicitly recorded on a tool part. This is
   * the only provider signal findServerForMcpUsage matches on: an
   * inferred providerId would let a custom server on a known provider
   * host bind to an unrelated saved catalog server.
   */
  explicitProviderId?: string;
  serverUrl?: string;
  /**
   * Backend saved-server key parsed from the tool registration name.
   * Stable across display-name edits, so when both sides carry it a
   * usage binds to the exact saved record even after a rename.
   */
  serverKey?: string;
}

interface TrackedUsedServer extends McpUsedServer {
  nameKey: string;
  urlKey: string | null;
}

function normalizeUrlKey(url: string | undefined): string | null {
  if (!url) return null;
  return url.trim().replace(/\/+$/, "").toLowerCase();
}

/**
 * Walks a message's tool parts in encounter order, invoking `visit` for
 * each MCP tool call (both live `toolEvents` and persisted `turns` carry
 * the same calls; visiting both is fine because callers dedupe by server).
 * `visit` returns true to stop the walk early.
 */
function walkMcpToolParts(
  message: AgentChatMessage,
  visit: (
    toolName: string,
    state: Record<string, unknown> | undefined,
  ) => boolean,
): boolean {
  if (message.toolEvents) {
    for (const event of message.toolEvents) {
      if (
        event.event_type !== "tool_start" &&
        event.event_type !== "tool_end"
      ) {
        continue;
      }
      if (visit(event.tool_name ?? "", event.state)) return true;
    }
  }
  if (message.turns) {
    for (const turn of message.turns) {
      for (const part of turn.parts) {
        if (part.part_type !== "tool_start" && part.part_type !== "tool_end") {
          continue;
        }
        if (visit(part.tool_name ?? "", part.state)) return true;
      }
    }
  }
  return false;
}

/**
 * Returns the distinct MCP servers whose tools were called during the
 * conversation, in first-use order. Entries are merged by normalized
 * display name; the URL or registration key only splits two same-name
 * servers apart when both sides carry one (legacy tool parts have
 * neither), since personal and team servers may legally share a display
 * name. Merging also backfills
 * the provider id and URL from later, richer parts so an entry renders
 * with the best identity seen anywhere in the transcript.
 * `resolveMcpUsageRows` collapses entries that turn out to be the same
 * saved server before rendering.
 */
export function detectMcpServersUsed(
  messages: AgentChatMessage[],
): McpUsedServer[] {
  const tracked: TrackedUsedServer[] = [];

  for (const message of messages) {
    walkMcpToolParts(message, (toolName, state) => {
      const mcp = extractMcpToolMetadata(toolName, state);
      if (!mcp) return false;

      const nameKey = normalizeMcpDisplayNameKey(mcp.serverName);
      const urlKey = normalizeUrlKey(mcp.serverUrl);
      const existing = tracked.find(
        (entry) =>
          entry.nameKey === nameKey &&
          (entry.urlKey === null ||
            urlKey === null ||
            entry.urlKey === urlKey) &&
          (entry.serverKey === undefined ||
            mcp.serverKey === undefined ||
            entry.serverKey === mcp.serverKey),
      );
      if (existing) {
        if (!existing.providerId && mcp.providerId) {
          existing.providerId = mcp.providerId;
        }
        if (!existing.explicitProviderId && mcp.explicitProviderId) {
          existing.explicitProviderId = mcp.explicitProviderId;
        }
        if (existing.urlKey === null && urlKey !== null) {
          existing.serverUrl = mcp.serverUrl;
          existing.urlKey = urlKey;
        }
        if (existing.serverKey === undefined && mcp.serverKey !== undefined) {
          existing.serverKey = mcp.serverKey;
        }
        return false;
      }
      tracked.push({
        serverName: mcp.serverName,
        providerId: mcp.providerId,
        explicitProviderId: mcp.explicitProviderId,
        serverUrl: mcp.serverUrl,
        serverKey: mcp.serverKey,
        nameKey,
        urlKey,
      });
      return false;
    });
  }

  return tracked.map(
    ({ serverName, providerId, explicitProviderId, serverUrl, serverKey }) => ({
      serverName,
      providerId,
      explicitProviderId,
      serverUrl,
      serverKey,
    }),
  );
}

type ServerEntry = [serverKey: string, server: McpServer];

export interface McpUsageServerMatch {
  serverKey: string;
  server: McpServer;
}

/**
 * Resolves a used server from the transcript to the plausible saved server
 * records. When the usage carries the registration key parsed from the
 * tool name (`s-{session}_{key}_{tool}`), that key IS the saved server's
 * backend key and is stable across renames, so an exact key match binds
 * the usage outright: a usage recorded under an old display name still
 * pins to the record's current identity. Without a key (legacy parts),
 * matching is heuristic: display name first, then the saved key, the URL,
 * and finally the provider slug (explicit only; a host-inferred providerId
 * is display-only and never a match signal). The first tier with any match
 * defines the candidate set, which the remaining signals then narrow;
 * enabled servers win over disabled duplicates because sessions only
 * attach enabled servers.
 *
 * When both sides carry a URL it is the stronger identity signal: a saved
 * server whose URL differs from the usage's never matches by name alone
 * (display names get reshuffled), so the URL tier can bind the row to the
 * record the session actually talked to.
 *
 * Several candidates can survive (e.g. same-URL personal and team
 * duplicates). Callers that ACT on a server (re-authorize, edit) must
 * require a unique match via `findServerForMcpUsage`; display-only callers
 * can still aggregate over the ambiguous set (e.g. a shared status dot).
 */
export function findServerCandidatesForMcpUsage(
  usage: McpUsedServer,
  servers: Record<string, McpServer>,
): McpUsageServerMatch[] {
  const entries = Object.entries(servers);
  const nameKey = normalizeMcpDisplayNameKey(usage.serverName);
  const usageUrl = normalizeUrlKey(usage.serverUrl);

  const matchesUrl = ([, server]: ServerEntry): boolean =>
    usageUrl !== null &&
    !!server.url &&
    normalizeUrlKey(server.url) === usageUrl;
  const contradictsUrl = ([, server]: ServerEntry): boolean =>
    usageUrl !== null &&
    !!server.url &&
    normalizeUrlKey(server.url) !== usageUrl;
  const matchesProvider = ([, server]: ServerEntry): boolean =>
    !!usage.explicitProviderId &&
    server.provider_id === usage.explicitProviderId;
  const isEnabled = ([, server]: ServerEntry): boolean => server.enabled;

  const tiers: ServerEntry[][] = [
    // Exact registration-key match: authoritative, so a URL edited since
    // the call deliberately does not contradict it.
    entries.filter(([serverKey]) => serverKey === usage.serverKey),
    entries.filter(
      (entry) =>
        normalizeMcpDisplayNameKey(entry[1].display_name ?? entry[0]) ===
          nameKey && !contradictsUrl(entry),
    ),
    entries.filter(
      (entry) =>
        normalizeMcpDisplayNameKey(entry[0]) === nameKey &&
        !contradictsUrl(entry),
    ),
    entries.filter(matchesUrl),
    entries.filter(matchesProvider),
  ];

  for (const tier of tiers) {
    if (tier.length === 0) continue;
    return narrowCandidates(tier, [matchesUrl, matchesProvider, isEnabled]).map(
      ([serverKey, server]) => ({ serverKey, server }),
    );
  }
  return [];
}

/**
 * Unique-match variant of `findServerCandidatesForMcpUsage`: a usage that
 * cannot be pinned to exactly one server resolves to null, so callers
 * never act against the wrong record.
 */
export function findServerForMcpUsage(
  usage: McpUsedServer,
  servers: Record<string, McpServer>,
): McpUsageServerMatch | null {
  const candidates = findServerCandidatesForMcpUsage(usage, servers);
  return candidates.length === 1 ? candidates[0] : null;
}

export interface McpUsageRow {
  /** Stable identity for React keys; unique across the returned rows. */
  key: string;
  used: McpUsedServer;
  /** Saved servers the usage plausibly refers to (may be empty). */
  candidates: McpUsageServerMatch[];
  /** The single saved server the row is pinned to, when unambiguous. */
  match: McpUsageServerMatch | null;
}

/**
 * Turns the transcript-derived usages into the rows the MCP widget
 * renders, deduplicated by resolved identity: two usages that map to the
 * same saved server(s) (e.g. a server whose URL was edited mid-session)
 * collapse into one row, while same-name usages that pin to different
 * records (a personal and a team server sharing a display name) stay
 * separate; the widget disambiguates those visually by scope.
 *
 * Same-name usages with NESTED candidate sets also collapse, keeping the
 * narrower non-empty set: a stale-URL usage resolves to nothing (or to a
 * broad provider-tier set) while the current-URL usage pins one server,
 * yet both refer to the same integration, and two rows for it would be
 * indistinguishable. Disjoint sets are the personal/team case and stay
 * apart. Usages that resolve to nothing (built-in servers, deleted
 * configs, or the saved-server list still loading) merge by display name
 * through the same rule (the empty set nests into anything).
 */
export function resolveMcpUsageRows(
  usedServers: McpUsedServer[],
  servers: Record<string, McpServer> | undefined,
): McpUsageRow[] {
  interface PendingRow {
    used: McpUsedServer;
    nameKey: string;
    candidates: McpUsageServerMatch[];
    keySet: Set<string>;
  }
  const pending: PendingRow[] = [];

  for (const used of usedServers) {
    const candidates = servers
      ? findServerCandidatesForMcpUsage(used, servers)
      : [];
    const nameKey = normalizeMcpDisplayNameKey(used.serverName);
    const keySet = new Set(candidates.map((candidate) => candidate.serverKey));

    const existing = pending.find(
      (row) =>
        // Same resolved identity, even under a name edited mid-session.
        (keySet.size > 0 &&
          keySet.size === row.keySet.size &&
          isSubset(keySet, row.keySet)) ||
        (row.nameKey === nameKey &&
          (isSubset(keySet, row.keySet) || isSubset(row.keySet, keySet))),
    );
    if (existing) {
      if (
        keySet.size > 0 &&
        (existing.keySet.size === 0 || keySet.size < existing.keySet.size)
      ) {
        // The narrowing usage is the row's identity from here on, so
        // render fallbacks (icon, URL) can't disagree with the pinned
        // server; provider fields backfill from the earlier usage.
        existing.used = {
          ...used,
          providerId: used.providerId ?? existing.used.providerId,
          explicitProviderId:
            used.explicitProviderId ?? existing.used.explicitProviderId,
        };
        existing.candidates = candidates;
        existing.keySet = keySet;
      }
      continue;
    }
    pending.push({ used, nameKey, candidates, keySet });
  }

  // Narrowing can make two rows converge on one identity after the fact;
  // a final pass by key keeps rows (and React keys) unique.
  const rows: McpUsageRow[] = [];
  const seen = new Set<string>();
  for (const { used, nameKey, candidates } of pending) {
    const key =
      candidates.length > 0
        ? `servers:${candidates
            .map((candidate) => candidate.serverKey)
            .sort()
            .join(",")}`
        : `name:${nameKey}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({
      key,
      used,
      candidates,
      match: candidates.length === 1 ? candidates[0] : null,
    });
  }

  return rows;
}

function isSubset(a: Set<string>, b: Set<string>): boolean {
  if (a.size > b.size) return false;
  return Array.from(a).every((item) => b.has(item));
}

/**
 * Narrows same-tier matches with the remaining signals. A narrower is only
 * applied when it keeps at least one entry; whatever set remains after all
 * narrowers is the candidate set (length 1 means an unambiguous match).
 */
function narrowCandidates(
  matches: ServerEntry[],
  narrowers: Array<(entry: ServerEntry) => boolean>,
): ServerEntry[] {
  let current = matches;
  for (const narrower of narrowers) {
    if (current.length === 1) break;
    const narrowed = current.filter(narrower);
    if (narrowed.length > 0) current = narrowed;
  }
  return current;
}

/**
 * Cheap existence check used by the sidebar-content gate: returns true as
 * soon as any tool part in the transcript resolves to an MCP tool, without
 * materialising the full server list.
 */
export function hasMcpToolUsage(messages: AgentChatMessage[]): boolean {
  for (const message of messages) {
    const found = walkMcpToolParts(
      message,
      (toolName, state) => extractMcpToolMetadata(toolName, state) !== null,
    );
    if (found) return true;
  }
  return false;
}
